import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { projectsAPI, usersAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Calendar, Users as UsersIcon, Edit2, Trash2, Euro, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = '/api';

// Health Score Badge Component
const HealthScoreBadge = ({ score, status }) => {
  if (!score && score !== 0) {
    return (
      <div className="flex items-center gap-1 text-xs text-slate-400">
        <Star className="w-3 h-3" />
        <span>-</span>
      </div>
    );
  }

  const statusColors = {
    excellent: 'bg-green-100 text-green-700 border-green-300',
    healthy: 'bg-blue-100 text-blue-700 border-blue-300',
    at_risk: 'bg-amber-100 text-amber-700 border-amber-300',
    critical: 'bg-red-100 text-red-700 border-red-300',
    no_data: 'bg-slate-100 text-slate-600 border-slate-300'
  };

  const stars = [];
  const fullStars = Math.floor(score);
  const hasHalf = score - fullStars >= 0.5;
  
  for (let i = 0; i < 5; i++) {
    if (i < fullStars) {
      stars.push(<Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />);
    } else if (i === fullStars && hasHalf) {
      stars.push(<Star key={i} className="w-3 h-3 fill-amber-400/50 text-amber-400" />);
    } else {
      stars.push(<Star key={i} className="w-3 h-3 text-slate-300" />);
    }
  }

  return (
    <div className={`flex items-center gap-2 px-2 py-1 rounded-md border ${statusColors[status] || statusColors.no_data}`}>
      <div className="flex items-center gap-0.5">{stars}</div>
      <span className="font-semibold text-sm">{score.toFixed(1)}</span>
    </div>
  );
};

const ProjectFormDialog = ({ project, isOpen, onClose, onSave, users, isAdmin }) => {
  const isEdit = !!project;
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    team_members: [],
    revenue_eur_monthly: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (project) {
        setFormData({
          name: project.name || '',
          description: project.description || '',
          start_date: project.start_date ? project.start_date.split('T')[0] : '',
          end_date: project.end_date ? project.end_date.split('T')[0] : '',
          team_members: project.team_members || [],
          revenue_eur_monthly: project.revenue_eur_monthly || ''
        });
      } else {
        setFormData({
          name: '',
          description: '',
          start_date: '',
          end_date: '',
          team_members: [],
          revenue_eur_monthly: ''
        });
      }
    }
  }, [isOpen, project]);

  const toggleTeamMember = (userId) => {
    setFormData(prev => ({
      ...prev,
      team_members: prev.team_members.includes(userId)
        ? prev.team_members.filter(id => id !== userId)
        : [...prev.team_members, userId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const payload = {
        ...formData,
        start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
        revenue_eur_monthly: formData.revenue_eur_monthly ? parseFloat(formData.revenue_eur_monthly) : null,
      };
      
      if (isEdit) {
        await projectsAPI.update(project.id, payload);
        toast.success('Proiectul a fost actualizat!');
      } else {
        await projectsAPI.create(payload);
        toast.success('Proiectul a fost creat!');
      }
      
      onSave();
      onClose();
    } catch (error) {
      toast.error(isEdit ? 'Eroare la actualizarea proiectului' : 'Eroare la crearea proiectului');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-outfit text-2xl">
            {isEdit ? 'Editează Proiect' : 'Proiect Nou'}
          </DialogTitle>
          <DialogDescription>
            {isEdit ? 'Modifică detaliile proiectului' : 'Completează informațiile pentru noul proiect'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nume Proiect *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="ex: Website Redesign"
              required
              data-testid="project-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Descriere</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              placeholder="Descriere scurtă a proiectului..."
              data-testid="project-description-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="start_date">Data Început</Label>
              <Input
                id="start_date"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                data-testid="project-start-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end_date">Data Sfârșit</Label>
              <Input
                id="end_date"
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                data-testid="project-end-date-input"
              />
            </div>
          </div>
          {isAdmin && (
            <div className="space-y-2">
              <Label htmlFor="revenue_eur_monthly">Revenue EUR/lună</Label>
              <div className="relative">
                <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="revenue_eur_monthly"
                  type="number"
                  step="0.01"
                  placeholder="Ex: 5000.00"
                  className="pl-9"
                  value={formData.revenue_eur_monthly}
                  onChange={(e) => setFormData({ ...formData, revenue_eur_monthly: e.target.value })}
                  data-testid="project-revenue-input"
                />
              </div>
              <p className="text-xs text-muted-foreground">Valoarea lunară contractată (pentru calculul profitabilității)</p>
            </div>
          )}
          <div className="space-y-2">
            <Label>Membrii Echipei</Label>
            <div className="border border-border rounded-md p-3 max-h-40 overflow-y-auto">
              {users.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-2">Nu există utilizatori</p>
              ) : (
                users.map(user => (
                  <label key={user.id} className="flex items-center gap-2 p-2 hover:bg-muted rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.team_members.includes(user.id)}
                      onChange={() => toggleTeamMember(user.id)}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <span className="text-sm">{user.full_name}</span>
                    <span className="text-xs text-muted-foreground">({user.email})</span>
                  </label>
                ))
              )}
            </div>
            <p className="text-xs text-muted-foreground">{formData.team_members.length} membri selectați</p>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Anulează
            </Button>
            <Button 
              type="submit" 
              className="bg-brand-orange hover:bg-brand-orange/90" 
              disabled={saving}
              data-testid="submit-project-btn"
            >
              {saving ? 'Se salvează...' : (isEdit ? 'Salvează Modificările' : 'Creează Proiect')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const Projects = () => {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [healthData, setHealthData] = useState({});
  const [showDialog, setShowDialog] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [deletingProject, setDeletingProject] = useState(null);
  const navigate = useNavigate();

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    loadProjects();
    loadUsers();
    loadHealthData();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await projectsAPI.getAll();
      setProjects(response.data);
    } catch (error) {
      toast.error('Eroare la încărcarea proiectelor');
    }
  };

  const loadUsers = async () => {
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  const loadHealthData = async () => {
    try {
      const token = localStorage.getItem('token');
      const currentMonth = new Date().toISOString().slice(0, 7);
      const response = await axios.get(`${API}/all-projects-health?month=${currentMonth}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Convert array to object keyed by project_id
      const healthMap = {};
      (response.data.projects || []).forEach(h => {
        healthMap[h.project_id] = h;
      });
      setHealthData(healthMap);
    } catch (error) {
      console.error('Failed to load health data');
    }
  };

  const handleCreate = () => {
    setEditingProject(null);
    setShowDialog(true);
  };

  const handleEdit = (e, project) => {
    e.stopPropagation();
    setEditingProject(project);
    setShowDialog(true);
  };

  const handleDelete = async (e, project) => {
    e.stopPropagation();
    if (window.confirm(`Ești sigur că vrei să ștergi proiectul "${project.name}"?`)) {
      try {
        await projectsAPI.delete(project.id);
        toast.success('Proiectul a fost șters');
        loadProjects();
      } catch (error) {
        toast.error('Eroare la ștergerea proiectului');
      }
    }
  };

  const handleSave = () => {
    loadProjects();
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-outfit text-4xl font-bold tracking-tight" data-testid="projects-title">Proiecte</h1>
            <p className="mt-2 text-muted-foreground">Gestionează toate proiectele într-un singur loc</p>
          </div>
          <Button 
            className="bg-brand-orange hover:bg-brand-orange/90" 
            onClick={handleCreate}
            data-testid="create-project-btn"
          >
            <Plus className="mr-2 h-4 w-4" />
            Proiect Nou
          </Button>
        </div>

        {projects.length === 0 ? (
          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                <p className="text-muted-foreground">Nu există proiecte. Creează primul proiect pentru a începe.</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <Card
                key={project.id}
                className="hover:shadow-md transition-smooth cursor-pointer group relative"
                onClick={() => navigate(`/projects/${project.id}`)}
                data-testid="project-card"
              >
                {/* Action buttons - visible on hover */}
                <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 z-10">
                  <Button
                    size="icon"
                    variant="secondary"
                    className="h-8 w-8"
                    onClick={(e) => handleEdit(e, project)}
                    data-testid={`edit-project-${project.id}`}
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  {isAdmin && (
                    <Button
                      size="icon"
                      variant="destructive"
                      className="h-8 w-8"
                      onClick={(e) => handleDelete(e, project)}
                      data-testid={`delete-project-${project.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                <CardHeader className="border-b border-border">
                  <div className="flex items-start justify-between">
                    <CardTitle className="font-outfit text-xl pr-16">{project.name}</CardTitle>
                    <HealthScoreBadge 
                      score={healthData[project.id]?.health_score} 
                      status={healthData[project.id]?.health_status}
                    />
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                    {project.description || 'Fără descriere'}
                  </p>
                  <div className="space-y-2 text-xs">
                    {isAdmin && project.revenue_eur_monthly && (
                      <div className="flex items-center gap-2 text-emerald-600 font-medium">
                        <Euro className="h-3 w-3" />
                        <span>{project.revenue_eur_monthly.toLocaleString()} EUR/lună</span>
                      </div>
                    )}
                    {project.start_date && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        <span>Început: {new Date(project.start_date).toLocaleDateString('ro-RO')}</span>
                      </div>
                    )}
                    {project.end_date && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        <span>Sfârșit: {new Date(project.end_date).toLocaleDateString('ro-RO')}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <UsersIcon className="h-3 w-3" />
                      <span>{project.team_members.length} membri în echipă</span>
                    </div>
                  </div>
                  <div className="mt-4">
                    <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium uppercase tracking-wide ${
                      project.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                      project.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                      'bg-slate-100 text-slate-700'
                    }`}>
                      {project.status === 'active' ? 'Activ' : 
                       project.status === 'completed' ? 'Finalizat' : project.status}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <ProjectFormDialog
          project={editingProject}
          isOpen={showDialog}
          onClose={() => {
            setShowDialog(false);
            setEditingProject(null);
          }}
          onSave={handleSave}
          users={users}
          isAdmin={isAdmin}
        />
      </div>
    </Layout>
  );
};
